import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactPixel from 'react-facebook-pixel';
import { trackTikTokEvent, initTikTokPixel } from '../providers/tiktokPixel';
import { getTranslations } from '../translations';
import { GEOS, getCurrentGeo } from '../geoConfig';

const Quiz = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const currentGeoId = getCurrentGeo();
  const geo = GEOS[currentGeoId] || GEOS.qa;

  // Language State: default from geo
  const [lang, setLang] = useState(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && geo.supportedLangs.includes(urlLang)) return urlLang;
    const savedLang = localStorage.getItem('dino_lang');
    if (savedLang && geo.supportedLangs.includes(savedLang)) return savedLang;
    return geo.defaultLang;
  });

  const t = getTranslations(currentGeoId, lang);

  useEffect(() => {
    localStorage.setItem('dino_lang', lang);
    localStorage.setItem('dino_geo', currentGeoId);
    document.documentElement.lang = lang;
    document.documentElement.dir = t.dir;
  }, [lang, t.dir, currentGeoId]);

  const toggleLanguage = () => {
    if (geo.supportedLangs.length <= 1) return;
    const nextLang = lang === geo.defaultLang ? (geo.supportedLangs.find(l => l !== geo.defaultLang) || 'en') : geo.defaultLang;
    setLang(nextLang);
    localStorage.setItem('dino_lang', nextLang);
  };

  // Single Universal VIP Pass Tier with dual attendee counters
  const [adultCount, setAdultCount] = useState(1);
  const [kidCount, setKidCount] = useState(0);

  const vipConfig = geo.tiersConfig?.vip || {
    options: {
      adult: { price: 76.5, standardPrice: 153.0 },
      kid: { price: 58.5, standardPrice: 117.0 }
    }
  };

  const adultPrice = vipConfig.options.adult.price;
  const adultStandardPrice = vipConfig.options.adult.standardPrice;
  const kidPrice = vipConfig.options.kid.price;
  const kidStandardPrice = vipConfig.options.kid.standardPrice;

  const totalTickets = adultCount + kidCount;
  const totalPrice = ((adultCount * adultPrice) + (kidCount * kidPrice)).toFixed(2);
  const totalStandardPrice = ((adultCount * adultStandardPrice) + (kidCount * kidStandardPrice)).toFixed(2);
  const totalDiscount = (parseFloat(totalStandardPrice) - parseFloat(totalPrice)).toFixed(2);

  const openPassValidity = t.openPassValidity;
  const openPassHours = t.openPassHours;

  const handleAdultMinus = () => {
    if (adultCount > 0 && totalTickets > 1) {
      setAdultCount(adultCount - 1);
    }
  };

  const handleAdultPlus = () => {
    if (adultCount < 15) {
      setAdultCount(adultCount + 1);
    }
  };

  const handleKidMinus = () => {
    if (kidCount > 0 && totalTickets > 1) {
      setKidCount(kidCount - 1);
    }
  };

  const handleKidPlus = () => {
    if (kidCount < 15) {
      setKidCount(kidCount + 1);
    }
  };

  useEffect(() => {
    // Facebook Pixel Init
    let p = new URLSearchParams(window.location.search).get('p');
    if (p) {
        p = p.replace(/\D/g, '');
        ReactPixel.init(p);
        ReactPixel.pageView();
        localStorage.setItem('pixel_p', p);
    } else {
        let cachedP = localStorage.getItem('pixel_p');
        if (cachedP) {
            cachedP = cachedP.replace(/\D/g, '');
            ReactPixel.init(cachedP);
            ReactPixel.pageView();
        }
    }

    // TikTok Pixel Init & ViewContent
    let tt = new URLSearchParams(window.location.search).get('tt');
    if (tt) {
        initTikTokPixel(tt);
    } else {
        initTikTokPixel();
    }
    trackTikTokEvent('ViewContent', {
        content_name: `${geo.name} Dino World VIP Tickets`,
        content_type: 'product',
        currency: geo.currencyCode
    });
  }, [geo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (totalTickets < 1) return;
    setLoading(true);
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Sanitize string inputs
    const firstName = (data.first_name || '').trim();
    const lastName = (data.last_name || '').trim();
    const email = (data.email || '').trim();

    // Client-side validation (safety net matching server-side rules)
    if (firstName.length < 2 || lastName.length < 2) {
      setLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setLoading(false);
      return;
    }
    const phoneDigits = (data.phone || '').replace(/\D/g, '');
    if (phoneDigits.length < 4) {
      setLoading(false);
      return;
    }
    
    // Clean phone number: extract digits and remove duplicate country prefix or leading 0
    const rawPhoneDigits = (data.phone || '').replace(/[^\d]/g, '');
    const prefixDigits = geo.phonePrefix.replace(/[^\d]/g, '');
    let cleanLocalDigits = rawPhoneDigits;
    if (cleanLocalDigits.startsWith(prefixDigits)) {
      cleanLocalDigits = cleanLocalDigits.slice(prefixDigits.length);
    }
    if (cleanLocalDigits.startsWith('0')) {
      cleanLocalDigits = cleanLocalDigits.slice(1);
    }

    const vipTransName = t.tiers?.vip?.name || (lang === 'ar' ? 'تذكرة كبار الشخصيات VIP الشاملة' : 'Universal VIP All-Access Pass');
    let breakdownStr = '';
    if (adultCount > 0 && kidCount > 0) {
      breakdownStr = `${adultCount} ${t.types.adult}, ${kidCount} ${t.types.kid}`;
    } else if (adultCount > 0) {
      breakdownStr = `${adultCount} ${t.types.adult}`;
    } else {
      breakdownStr = `${kidCount} ${t.types.kid}`;
    }
    const fullTierName = `${vipTransName} (${breakdownStr})`;

    data.first_name = firstName;
    data.last_name = lastName;
    data.email = email;
    data.country_code = geo.phonePrefix;
    data.phone = cleanLocalDigits;
    data.country = geo.name;
    data.geo = currentGeoId;
    data.selectedTier = 'vip';
    data.tierName = fullTierName;
    data.ticketCount = totalTickets;
    data.adultCount = adultCount;
    data.kidCount = kidCount;
    data.selectedDate = openPassValidity;
    data.timeSlot = openPassHours;
    data.totalPrice = totalPrice;
    data.currency = geo.currencyCode;
    data.lang = lang;

    // Extended fields matching worker requirements
    data.company = `${data.tierName} (${totalTickets} ticket${totalTickets > 1 ? 's' : ''})`;
    data.job_title = `Open Pass • ${openPassHours}`;
    data.job_function = `${data.totalPrice} ${geo.currencyCode}`;
    data.industry = geo.venue?.en || t.venueName || 'Dino World Festival 2026';
    data.business_size = `Booked at ${new Date().toLocaleTimeString('en-GB')}`;
    data.chat_id = geo.chatId || geo.chat_id || process.env.REACT_APP_CHAT_ID || '-1004401646290';

    localStorage.setItem('dino_lang', lang);
    localStorage.setItem('dino_geo', currentGeoId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userFirstName', firstName);
    localStorage.setItem('userLastName', lastName);
    localStorage.setItem('userPhone', `${geo.phonePrefix} ${cleanLocalDigits}`);
    localStorage.setItem('selectedTier', 'vip');
    localStorage.setItem('tierName', fullTierName);
    localStorage.setItem('ticketCount', totalTickets.toString());
    localStorage.setItem('adultCount', adultCount.toString());
    localStorage.setItem('kidCount', kidCount.toString());
    localStorage.setItem('selectedDate', openPassValidity);
    localStorage.setItem('timeSlot', openPassHours);
    localStorage.setItem('summ', totalPrice);
    localStorage.setItem('currency', geo.currencyCode);

    // Strict pixel policy: Fire CompleteRegistration and Lead ONCE on form submit
    try {
      ReactPixel.track('CompleteRegistration');
      ReactPixel.track('Lead');
    } catch(err) {
      console.error('Pixel error:', err);
    }

    // TikTok Pixel Events: AddToCart & InitiateCheckout
    try {
      trackTikTokEvent('AddToCart', {
        content_id: 'vip',
        content_name: fullTierName,
        content_type: 'product',
        quantity: totalTickets,
        value: parseFloat(totalPrice) || 0,
        currency: geo.currencyCode
      });
      trackTikTokEvent('InitiateCheckout', {
        content_id: 'vip',
        content_type: 'product',
        quantity: totalTickets,
        value: parseFloat(totalPrice) || 0,
        currency: geo.currencyCode
      });
    } catch (err) {
      console.error('TikTok track error:', err);
    }

    // Send to worker asynchronously with max 1.2s timeout so mobile networks never freeze
    try {
      const workerUrl = process.env.REACT_APP_WORKER_URL || 'https://event-ticket-register.live/api/ticket';
      const workerFetch = fetch(workerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
      }).catch(err => console.warn('Worker post error:', err));

      await Promise.race([
        workerFetch,
        new Promise(resolve => setTimeout(resolve, 1200))
      ]);
    } catch(err) {}

    // Direct routing to checkout for high-conversion reservation flow
    navigate('/checkout');
  };

  const inclusionsList = t.vipInclusions || (t.tiers?.vip?.inclusions || []);

  return (
    <div className={`min-h-screen bg-[#0a130e] text-[#f5f1e8] font-['Cairo',sans-serif] selection:bg-[#fbc61c] selection:text-black ${lang === 'en' ? 'font-sans' : ''}`} dir={t.dir}>
      
      {/* Top Header Bar */}
      <div className="bg-[#0f2418] border-b border-[#fbc61c]/20 py-3.5 px-4 sticky top-0 z-50 backdrop-blur-md bg-opacity-95">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          
          {/* Brand / Logo */}
          <div className="flex items-center gap-3">
            <a href={`index.html?lang=${lang}`} className="cursor-pointer hover:opacity-80 transition-opacity">
              <img 
                src="images/dinoworld-logo.png" 
                alt="Dino World" 
                className="h-8 md:h-10 w-auto object-contain"
                onError={(e) => { e.target.src = "fm_nl/Campagnes/dino-world/dinoworld-logo.png"; }}
              />
            </a>
            <span className="hidden sm:inline-block bg-[#fbc61c]/15 text-[#fbc61c] text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-[#fbc61c]/30">
              {t.countryBadge}
            </span>
          </div>

          {/* Right Side: Venue & Language Switcher */}
          <div className="flex items-center gap-2 md:gap-3">
            <span className="text-[11px] md:text-xs font-semibold text-slate-300 hidden md:block">
              {t.venueName}
            </span>

            {/* STYLED DINO GRUNGE LANGUAGE TOGGLE BUTTON */}
            {geo.supportedLangs.length > 1 && (
              <button 
                type="button"
                onClick={toggleLanguage}
                className="dino-lang-grunge-btn"
                title="Switch Language"
              >
                <span>{t.langToggleLabel}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 md:py-10">
        
        {/* Distressed Header Banner Ribbon */}
        <div className="text-center mb-8">
          <div className="inline-block dino-grunge-ribbon px-6 py-2 mb-3">
            <span className="text-black text-sm md:text-base font-black tracking-wider uppercase">
              {t.headerTitle}
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-black text-white mb-2 leading-tight">
            {t.step1Title}
          </h1>
          <p className="text-slate-300 text-xs md:text-sm max-w-xl mx-auto">
            {t.venueName} • <span className="text-[#fbc61c] font-bold">{t.eventDatesText}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* ================= STEP 1: VIP TICKET & ATTENDEE SELECTION ================= */}
          <div className="bg-[#0f2418]/85 border-2 border-[#fbc61c]/35 rounded-3xl p-5 md:p-8 shadow-2xl backdrop-blur-md relative overflow-hidden">
            
            {/* Top Glow Accent */}
            <div className="absolute -top-20 right-1/2 translate-x-1/2 w-96 h-32 bg-[#fbc61c]/10 blur-3xl pointer-events-none"></div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-[#fbc61c] text-black font-black flex items-center justify-center text-lg shadow-lg">1</span>
                <div>
                  <h2 className="text-lg md:text-xl font-black text-white">{t.step1Title}</h2>
                  <p className="text-xs text-slate-300">{t.step1Subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-start sm:self-auto">
                <span className="dino-badge-discount px-3.5 py-1 text-xs font-black shadow-md">
                  {t.discount50Badge || '-50% OFF'}
                </span>
              </div>
            </div>

            {/* VIP Pass Main Showcase Card */}
            <div className="bg-gradient-to-br from-[#183a27] via-[#122b1d] to-[#0d1f15] border-2 border-[#fbc61c] rounded-2xl p-5 md:p-6 mb-6 shadow-2xl relative">
              
              {/* Badge Top Corner */}
              <div className="flex items-center justify-between gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 bg-[#fbc61c] text-black text-xs md:text-sm font-black px-3.5 py-1 rounded-full shadow-lg">
                  <svg className="w-3.5 h-3.5 text-black fill-current inline-block flex-shrink-0" style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span>{t.vipPassBadge || 'OFFICIAL VIP PASS • 50% OFF'}</span>
                </span>
                <span className="text-[11px] font-bold text-[#fbc61c] uppercase tracking-wider bg-black/40 px-2.5 py-1 rounded-md border border-[#fbc61c]/30">
                  {lang === 'ar' ? 'شامل لجميع الفعاليات' : 'ALL-INCLUSIVE ACCESS'}
                </span>
              </div>

              {/* Title & Description */}
              <h3 className="text-xl md:text-2xl font-black text-white mb-2">
                {t.vipCardTitle || t.tiers?.vip?.name || 'Universal VIP All-Access Pass'}
              </h3>
              <p className="text-xs md:text-sm text-[#fbc61c] font-semibold mb-5 leading-relaxed">
                {t.vipCardTag || t.tiers?.vip?.tag || 'Priority Fast-Track + All 5 Zones + Live Circus Show + Dino Rides + Souvenir Gifts'}
              </p>

              {/* Inclusions Grid */}
              <div className="bg-black/30 rounded-xl p-4 border border-white/10 mb-2">
                <h4 className="text-xs font-bold text-slate-200 mb-3 flex items-center gap-2">
                  <svg className="w-3.5 h-3.5 text-[#fbc61c] fill-current inline-block flex-shrink-0" style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 0L14.7 9.3L24 12L14.7 14.7L12 24L9.3 14.7L0 12L9.3 9.3L12 0Z"/>
                  </svg>
                  <span>{t.vipInclusionsTitle || 'Everything Included in Your VIP Pass:'}</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {inclusionsList.map((inc, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-slate-200">
                      <svg className="w-4 h-4 text-[#fbc61c] flex-shrink-0 mt-0.5" style={{ width: '16px', height: '16px' }} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      <span className="leading-snug">{inc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* DUAL COUNTERS: ADULTS & CHILDREN */}
            <div className="bg-[#0a1810]/90 border border-white/15 rounded-2xl p-4 md:p-6 shadow-inner">
              <div className="mb-4">
                <h4 className="text-sm md:text-base font-black text-white flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#fbc61c] inline-block flex-shrink-0" style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  <span>{lang === 'ar' ? 'حدد عدد التذاكر (كبار وصغار):' : (lang === 'zh' ? '選擇門票數量 (成人與兒童)：' : (lang === 'id' ? 'Pilih Jumlah Tiket (Dewasa & Anak-anak):' : 'Select Attendees (Adults & Children):'))}</span>
                </h4>
                <p className="text-xs text-slate-400 mt-0.5">
                  {lang === 'ar' ? 'الأسعار تشمل خصم 50% الفوري لجميع التذاكر المختارة' : (lang === 'zh' ? '所有所選門票均享 50% 專屬早鳥折扣' : (lang === 'id' ? 'Semua tiket otomatis mendapatkan diskon kilat 50%' : 'All tickets automatically include the 50% flash discount'))}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* ADULTS COUNTER CARD */}
                <div className="bg-[#0f2418] border-2 border-[#fbc61c]/40 hover:border-[#fbc61c] rounded-2xl p-4 md:p-5 transition-all shadow-md flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm md:text-base font-black text-white">
                        {t.adultsLabel || 'Adult Ticket'}
                      </span>
                      <span className="dino-badge-discount text-[10px] px-2 py-0.5 font-black">
                        -50%
                      </span>
                    </div>
                    
                    <p className="text-[11px] text-slate-400 mb-3">
                      {t.adultsAge || 'Ages 13+ • Full VIP Inclusions'}
                    </p>

                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-xs text-slate-400 line-through">
                        {adultStandardPrice.toFixed(2)} {t.currency}
                      </span>
                      <span className="text-lg md:text-xl font-black text-[#fbc61c]">
                        {adultPrice.toFixed(2)} {t.currency}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {t.perTicket || '/ pass'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <span className="text-xs font-bold text-slate-300">
                      {t.ticketsCountLabel || 'Quantity:'}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleAdultMinus}
                        disabled={adultCount <= 1 && totalTickets <= 1}
                        className="w-9 h-9 rounded-lg bg-[#183e28] hover:bg-[#225738] active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed text-[#fbc61c] font-black text-lg flex items-center justify-center cursor-pointer transition-all border border-white/10"
                        title="Decrease Adults"
                      >
                        –
                      </button>
                      <span className="w-8 text-center font-black text-lg md:text-xl text-white">
                        {adultCount}
                      </span>
                      <button
                        type="button"
                        onClick={handleAdultPlus}
                        disabled={adultCount >= 15}
                        className="w-9 h-9 rounded-lg bg-[#183e28] hover:bg-[#225738] active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed text-[#fbc61c] font-black text-lg flex items-center justify-center cursor-pointer transition-all border border-white/10"
                        title="Increase Adults"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* CHILDREN COUNTER CARD */}
                <div className="bg-[#0f2418] border-2 border-[#fbc61c]/40 hover:border-[#fbc61c] rounded-2xl p-4 md:p-5 transition-all shadow-md flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm md:text-base font-black text-white">
                        {t.kidsLabel || 'Child Ticket'}
                      </span>
                      <span className="dino-badge-discount text-[10px] px-2 py-0.5 font-black">
                        -50%
                      </span>
                    </div>

                    <p className="text-[11px] text-slate-400 mb-3">
                      {t.kidsAge || 'Ages 3-12 (Under 3 Free)'}
                    </p>

                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-xs text-slate-400 line-through">
                        {kidStandardPrice.toFixed(2)} {t.currency}
                      </span>
                      <span className="text-lg md:text-xl font-black text-[#fbc61c]">
                        {kidPrice.toFixed(2)} {t.currency}
                      </span>
                      <span className="text-[11px] text-slate-400">
                        {t.perTicket || '/ pass'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-white/10">
                    <span className="text-xs font-bold text-slate-300">
                      {t.ticketsCountLabel || 'Quantity:'}
                    </span>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleKidMinus}
                        disabled={kidCount === 0 || (totalTickets <= 1 && kidCount === 1)}
                        className="w-9 h-9 rounded-lg bg-[#183e28] hover:bg-[#225738] active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed text-[#fbc61c] font-black text-lg flex items-center justify-center cursor-pointer transition-all border border-white/10"
                        title="Decrease Children"
                      >
                        –
                      </button>
                      <span className="w-8 text-center font-black text-lg md:text-xl text-white">
                        {kidCount}
                      </span>
                      <button
                        type="button"
                        onClick={handleKidPlus}
                        disabled={kidCount >= 15}
                        className="w-9 h-9 rounded-lg bg-[#183e28] hover:bg-[#225738] active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed text-[#fbc61c] font-black text-lg flex items-center justify-center cursor-pointer transition-all border border-white/10"
                        title="Increase Children"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

              </div>

              {/* Total Tickets Summary Notice & Flexible Pass Banner */}
              <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-300">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#fbc61c] animate-pulse"></span>
                  <span>
                    {lang === 'ar' ? 'إجمالي التذاكر المحجوزة:' : (lang === 'id' ? 'Total Tiket Direservasi:' : 'Total Reserved Passes:')} <strong className="text-white font-black">{totalTickets}</strong> ({adultCount} {t.types.adult}, {kidCount} {t.types.kid})
                  </span>
                </div>
                <span className="text-[11px] text-[#fbc61c] font-semibold">
                  {lang === 'ar' ? 'الأطفال دون سنتين مجاناً بدون تذكرة' : (lang === 'id' ? 'Anak di bawah 3 tahun masuk gratis tanpa tiket' : 'Children under 3 enter free without a ticket')}
                </span>
              </div>

              {/* 100% Flexible Entry Callout */}
              <div className="mt-3.5 bg-gradient-to-r from-[#143322] via-[#1a442d] to-[#143322] border border-[#fbc61c]/40 rounded-xl p-3 text-center flex items-center justify-center gap-2.5">
                <svg className="w-5 h-5 text-[#fbc61c] flex-shrink-0" style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                </svg>
                <span className="text-xs font-bold text-amber-200">
                  {t.openPassBanner}
                </span>
              </div>
            </div>

          </div>

          {/* ================= STEP 2: CONTACT & RESERVATION ================= */}
          <div className="bg-[#0f2418]/80 border border-[#fbc61c]/25 rounded-3xl p-5 md:p-7 shadow-2xl backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-8 rounded-xl bg-[#fbc61c] text-black font-black flex items-center justify-center text-base shadow-md">2</span>
              <div>
                <h2 className="text-lg md:text-xl font-black text-white">{t.step2Title || (lang === 'ar' ? 'بيانات الحجز واستلام التذاكر' : 'Attendee Details & Ticket Delivery')}</h2>
                <p className="text-xs text-slate-400">{t.step2Subtitle || (lang === 'ar' ? 'ستصلك التذاكر الإلكترونية مع باركود الدخول السريع فوراً' : 'Your fast-track e-tickets will be delivered instantly')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1.5">{t.form.firstName} *</label>
                <input
                  type="text"
                  name="first_name"
                  required
                  minLength={2}
                  placeholder={lang === 'ar' ? 'مثال: محمد' : (lang === 'id' ? 'cth. Budi' : 'e.g. John')}
                  className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1.5">{t.form.lastName} *</label>
                <input
                  type="text"
                  name="last_name"
                  required
                  minLength={2}
                  placeholder={lang === 'ar' ? 'مثال: الكواري' : (lang === 'id' ? 'cth. Santoso' : 'e.g. Smith')}
                  className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1.5">{t.form.email} *</label>
                <input
                  type="email"
                  name="email"
                  required
                  pattern="[^\s@]+@[^\s@]+\.[^\s@]{2,}"
                  title="Please enter a valid email address"
                  placeholder="name@domain.com"
                  className="w-full bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all text-left"
                  dir="ltr"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-300 block mb-1.5">{t.form.phone} *</label>
                <div className="flex gap-2">
                  <span className="bg-[#09170f] border border-white/15 rounded-xl px-3 py-3 text-sm font-mono text-slate-300 flex items-center justify-center font-bold">
                    {geo.phonePrefix}
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    required
                    minLength={4}
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder={geo.phonePlaceholder}
                    className="flex-1 bg-[#09170f] border border-white/15 focus:border-[#fbc61c] rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 outline-none transition-all font-mono text-left"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ================= ORDER SUMMARY & SUBMIT ================= */}
          <div className="bg-gradient-to-b from-[#133020] to-[#0c1f15] border-2 border-[#fbc61c] rounded-3xl p-6 md:p-8 shadow-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-6 border-b border-white/15">
              <div>
                <span className="text-xs text-slate-300 block mb-1">{lang === 'ar' ? 'الباقة وصلاحية الدخول:' : 'Package & Ticket Validity:'}</span>
                <h3 className="text-lg md:text-xl font-black text-white">
                  {t.tiers?.vip?.name || 'Universal VIP Pass'}
                </h3>
                <p className="text-xs text-slate-300 font-medium mt-1">
                  {adultCount > 0 ? `${adultCount} ${t.types.adult}` : ''}{adultCount > 0 && kidCount > 0 ? ' • ' : ''}{kidCount > 0 ? `${kidCount} ${t.types.kid}` : ''}
                </p>
                <p className="text-xs text-[#fbc61c] font-semibold mt-1 flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-[#fbc61c] flex-shrink-0 inline-block" style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                  </svg>
                  <span>{openPassValidity} • {openPassHours}</span>
                </p>
              </div>

              {/* Price Calculation Box */}
              <div className="text-center md:text-right bg-[#09160e] border border-white/10 rounded-2xl px-6 py-4">
                <div className="flex items-center justify-center md:justify-end gap-2 mb-1">
                  <span className="text-xs text-slate-400">{t.standardTotal}</span>
                  <span className="text-sm text-slate-400 line-through">{totalStandardPrice} {t.currency}</span>
                </div>
                <div className="flex items-center justify-center md:justify-end gap-2 mb-1">
                  <span className="text-xs text-emerald-400 font-bold">{t.saveText}</span>
                  <span className="text-xs font-black text-emerald-400">-{totalDiscount} {t.currency} (-50%)</span>
                </div>
                <div className="text-2xl md:text-3xl font-black text-[#fbc61c]">
                  {totalPrice} <span className="text-sm">{t.currency}</span>
                </div>
              </div>
            </div>

            {/* Guarantees */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-6 text-center">
              {(t.guarantees || []).map((g, i) => (
                <div key={i} className="bg-black/25 rounded-xl p-3 border border-white/5">
                  <h5 className="text-xs font-bold text-white mb-0.5">{g.title}</h5>
                  <p className="text-[10px] text-slate-400 leading-tight">{g.desc}</p>
                </div>
              ))}
            </div>

            {/* Big Distressed Submit Button */}
            <button
              type="submit"
              disabled={loading || totalTickets < 1}
              className="w-full dino-grunge-btn py-4 md:py-5 rounded-2xl font-black text-lg md:text-2xl flex items-center justify-center gap-3 shadow-2xl disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <span>{t.processingBtn}</span>
              ) : (
                <>
                  <svg className="w-6 h-6 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
                  <span>{t.continueBtn}</span>
                </>
              )}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default Quiz;
